import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Plus, User } from "lucide-react";
import { PageLoading } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const STATUS_C = { DRAFT: "bg-slate-100 text-slate-500", PLANNED: "bg-cyan/10 text-cyan-dark", READY: "bg-emerald-50 text-emerald-600", IN_PROGRESS: "bg-blue-50 text-blue-600", COMPLETED: "bg-slate-100 text-slate-500", CANCELLED: "bg-red-50 text-red-600", REVIEW_REQUIRED: "bg-amber-50 text-amber-700" };

export default function CareTours() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", assigned_worker_id: "", planned_start_time: "07:00", planned_end_time: "12:00" });
  const nav = useNavigate();

  const load = () => api.get(`/care/tours?date=${date}`).then((r) => setData(r.data)).catch(() => setData({ tours: [] }));
  useEffect(() => { setData(null); load(); }, [date]);
  useEffect(() => { api.get("/care/workers").then((r) => setWorkers(r.data.workers)).catch(() => {}); }, []);

  const create = async () => {
    if (!f.name) { toast.error("Name erforderlich"); return; }
    try {
      const r = await api.post("/care/tours", { name: f.name, date, assigned_worker_id: f.assigned_worker_id || null, planned_start_time: f.planned_start_time, planned_end_time: f.planned_end_time });
      toast.success(`Tour ${r.data.tour_number} erstellt`); setOpen(false); nav(`/app/employer/pflege/tours/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="care-tours-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><MapPin className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Touren</h1></div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="tours-date" />
          <button onClick={() => setOpen(true)} data-testid="tours-new" className="inline-flex items-center gap-1.5 rounded-full bg-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-dark"><Plus className="h-4 w-4" /> Neue Tour</button>
        </div>
      </div>

      {!data ? <PageLoading /> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.tours.map((tr) => (
            <button key={tr.id} onClick={() => nav(`/app/employer/pflege/tours/${tr.id}`)} className="rounded-2xl border border-slate-200 bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md" data-testid={`tour-card-${tr.id}`}>
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[tr.status]}`}>{tr.status_label}</span>
                <span className="font-mono text-[11px] text-slate-400">{tr.tour_number}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-extrabold text-navy">{tr.name}</h3>
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-500"><User className="h-4 w-4" />{tr.worker_name || "Nicht zugewiesen"}</div>
              <div className="mt-1 text-sm text-slate-500">{tr.planned_start_time}–{tr.planned_end_time} · {tr.visit_count} Besuche</div>
            </button>
          ))}
          {data.tours.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="tours-empty">Keine Touren an diesem Tag.</div>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tour-create-dialog">
          <DialogHeader><DialogTitle>Neue Tour · {date}</DialogTitle><DialogDescription>Erstellen Sie eine Tour und weisen Sie danach Besuche zu.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">Name *</span><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Frühdienst A" data-testid="tour-name" /></label>
            <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">Mitarbeiter/in</span>
              <select value={f.assigned_worker_id} onChange={(e) => setF({ ...f, assigned_worker_id: e.target.value })} data-testid="tour-worker" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <option value="">— optional —</option>{workers.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.care_role_label})</option>)}
              </select></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">Start</span><Input type="time" value={f.planned_start_time} onChange={(e) => setF({ ...f, planned_start_time: e.target.value })} /></label>
              <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">Ende</span><Input type="time" value={f.planned_end_time} onChange={(e) => setF({ ...f, planned_end_time: e.target.value })} /></label>
            </div>
            <Button onClick={create} className="w-full rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="tour-create-submit">Tour erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
